import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Trash2, AlertTriangle, CheckCircle2,
  Mail, Phone, User, Shield, Database, Clock
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const AccountDeletion = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    requestType: 'delete-account', reason: '', additionalInfo: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', requestType: 'delete-account', reason: '', additionalInfo: '' });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-20 w-72 h-72 bg-orange-500/15 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 left-20 w-56 h-56 bg-blue-600/15 rounded-full blur-[60px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Trash2 className="w-4 h-4" /> Account & Data Management
            </span>
            <h1 className="text-5xl font-bold text-white mb-4">MeritX Account Deletion</h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Request deletion of your MeritX account and data in compliance with Google Play and privacy regulations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 space-y-8">

        {/* info cards */}
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Trash2, color: 'bg-red-500/15 text-red-400', title: 'Delete Account', desc: 'Permanently remove your account and all associated data.' },
            { icon: Database, color: 'bg-blue-500/15 text-blue-400', title: 'Export Data', desc: 'Download a copy of your personal information first.' },
            { icon: Clock, color: 'bg-green-500/15 text-green-400', title: '24–48 Hours', desc: 'Typical processing time for all requests.' },
          ].map(({ icon: Icon, color, title, desc }) => (
            <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="bg-[#0C1628] border border-white/10 rounded-2xl p-6 text-center">
              <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-white mb-1">{title}</h3>
              <p className="text-slate-400 text-sm">{desc}</p>
            </motion.div>
          ))}
        </div>

        {/* how to */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-8">
          <h3 className="font-bold text-white mb-4 text-lg">How to Request Deletion</h3>
          <ol className="space-y-3 text-slate-300 text-sm">
            {[
              'Fill out the form below with your name, email, and phone linked to your MeritX account.',
              'Select your request type (Delete Account, Delete Specific Data, or Export Data).',
              "Submit the form — you'll receive a confirmation email immediately.",
              'Our team will verify your identity and process the request within 24–48 hours.',
              'You\'ll receive a final confirmation once deletion is complete.',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="font-bold text-orange-400 flex-shrink-0">Step {i + 1}:</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <p className="text-sm text-slate-400 mt-4">Alternatively, email <strong className="text-white">officecalloc@gmail.com</strong> or call <strong className="text-white">+91 8971504091</strong>.</p>
        </motion.div>

        {/* what gets deleted */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#0C1628] border border-white/10 rounded-2xl p-8">
          <h3 className="font-bold text-white mb-5 text-lg">What Data Will Be Deleted?</h3>

          <div className="mb-5">
            <h4 className="font-semibold text-green-400 mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Permanently Deleted:</h4>
            <ul className="text-sm text-slate-400 space-y-1 ml-6 list-disc">
              {['Profile information (name, email, phone, photo)', 'All test history and assessment records', 'Performance analytics and progress tracking', 'Rankings and leaderboard entries', 'Subscription history and payment records', 'Chat history and support conversations', 'Device tokens and notification preferences'].map(t => <li key={t}>{t}</li>)}
            </ul>
          </div>

          <div className="mb-5">
            <h4 className="font-semibold text-orange-400 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Retained as Required by Law:</h4>
            <ul className="text-sm text-slate-400 space-y-1 ml-6 list-disc">
              {['Transaction records for financial auditing (7 years per Indian tax law)', 'Anonymized analytics data (cannot be linked back to you)', 'Basic account metadata for fraud prevention (email hash, 90 days)'].map(t => <li key={t}>{t}</li>)}
            </ul>
          </div>

          <div className="bg-[#080F1E] rounded-xl p-5">
            <h4 className="font-semibold text-white mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Deletion Timeline:</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              {[['Immediate', 'Account deactivated and login disabled'], ['Within 48 hours', 'Personal data deleted from active databases'], ['Within 30 days', 'Data removed from backup systems'], ['90 days', 'Fraud prevention metadata deleted'], ['7 years', 'Financial records (if applicable, as required by law)']].map(([t, d]) => (
                <li key={t}><strong className="text-white">{t}:</strong> {d}</li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* warning */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="border-l-4 border-orange-400 bg-orange-500/10 rounded-r-2xl p-6 flex gap-4">
          <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-white mb-2">Important Warnings</h3>
            <ul className="text-sm text-slate-300 space-y-1">
              {['Account deletion is permanent and irreversible.', 'You will lose access to all MeritX features and content.', 'Active subscriptions will be cancelled (no refunds for unused time).', 'You cannot create a new account with the same email for 90 days.', 'This action applies only to MeritX by CALLOC Technologies.'].map(w => <li key={w}>• {w}</li>)}
            </ul>
          </div>
        </motion.div>

        {/* form */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#0C1628] border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-6">Submit Your Request</h2>

          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Request Submitted!</h3>
              <p className="text-slate-400 text-sm max-w-sm mx-auto mb-6">We'll process your request within 24–48 hours and send a confirmation to your email.</p>
              <button onClick={() => setSubmitted(false)} className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-3 rounded-xl font-semibold text-sm transition-colors">
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {[
                { label: 'Full Name', name: 'name', type: 'text', icon: User, placeholder: 'Your full name' },
                { label: 'Email Address', name: 'email', type: 'email', icon: Mail, placeholder: 'your@email.com' },
                { label: 'Phone Number', name: 'phone', type: 'tel', icon: Phone, placeholder: '+91 1234567890' },
              ].map(({ label, name, type, icon: Icon, placeholder }) => (
                <div key={name}>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">{label} *</label>
                  <div className="relative">
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input type={type} name={name} value={formData[name]} onChange={handleChange} required
                      placeholder={placeholder}
                      className="w-full pl-10 pr-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                </div>
              ))}

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Request Type *</label>
                <select name="requestType" value={formData.requestType} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                  <option value="delete-account">Delete My MeritX Account (Permanent)</option>
                  <option value="delete-specific-data">Delete Specific Data Only (Keep Account)</option>
                  <option value="export-data">Export My Data</option>
                  <option value="update-data">Update My Data</option>
                  <option value="access-data">Access My Data</option>
                  <option value="restrict-processing">Restrict Data Processing</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Reason (optional)</label>
                <select name="reason" value={formData.reason} onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                  <option value="">Select a reason</option>
                  <option value="no-longer-needed">No longer need the service</option>
                  <option value="privacy-concerns">Privacy concerns</option>
                  <option value="switching-service">Switching to another service</option>
                  <option value="too-many-emails">Too many emails</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Additional Information</label>
                <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="4"
                  placeholder="Any additional details..."
                  className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none" />
              </div>

              <button type="submit" disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white py-4 rounded-xl font-semibold text-sm transition-colors">
                {isSubmitting ? 'Submitting…' : 'Submit Request'}
              </button>
              <p className="text-xs text-slate-500 text-center">By submitting, you confirm you are the MeritX account owner and authorise CALLOC Technologies to process this request.</p>
            </form>
          )}
        </motion.div>

        {/* help */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#080F1E] border border-white/10 rounded-2xl p-8 flex gap-4">
          <Shield className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-white mb-2">Need Help?</h3>
            <p className="text-slate-400 text-sm mb-3">If you have questions about deleting your MeritX account, contact us directly:</p>
            <div className="space-y-1 text-sm text-slate-400">
              {[['App', 'MeritX (by CALLOC Technologies)'], ['Email', 'officecalloc@gmail.com'], ['Phone', '+91 8971504091'], ['Response Time', 'Within 24 hours']].map(([k, v]) => (
                <p key={k}><span className="text-white font-medium">{k}:</span> {v}</p>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default AccountDeletion;
